import { Controller, useFormContext } from 'react-hook-form';

import { Flex, FormControl, FormErrorMessage, FormLabel, Input, InputGroup } from '@chakra-ui/react';

import { formatDate, isDefined, isNotDefined } from '@enterprise/core';

import type { BillingForm } from './types/billing-form.type';

const BillingDatePicker = () => {
  const {
    control,
    formState: { errors },
    trigger,
    watch,
  } = useFormContext<BillingForm>();

  const startDate = watch('startDate');
  const endDate = watch('endDate');

  return (
    <Flex
      direction="column"
      gap={3}>
      <FormControl
        isInvalid={isDefined(errors.startDate)}
        isRequired>
        <FormLabel>Start Date</FormLabel>
        <InputGroup>
          <Controller
            control={control}
            name="startDate"
            render={({ field: { onChange, value, ...rest } }) => {
              return (
                <Input
                  {...rest}
                  max={isDefined(endDate) ? formatDate(endDate, 'yyyy-MM-dd') : undefined}
                  onChange={(e) => {
                    onChange(e.target.value ? new Date(`${e.target.value}T00:00:00`) : undefined);

                    void trigger('endDate');
                  }}
                  type="date"
                  value={isDefined(value) ? formatDate(value, 'yyyy-MM-dd') : ''}
                />
              );
            }}
            rules={{ required: 'Start Date is required' }}
          />
        </InputGroup>
        <FormErrorMessage>{errors.startDate?.message}</FormErrorMessage>
      </FormControl>
      <FormControl
        isInvalid={isDefined(errors.endDate)}
        isRequired>
        <FormLabel>End Date</FormLabel>
        <InputGroup>
          <Controller
            control={control}
            name="endDate"
            render={({ field: { onChange, value, ...rest } }) => {
              return (
                <Input
                  {...rest}
                  min={isDefined(startDate) ? formatDate(startDate, 'yyyy-MM-dd') : undefined}
                  onChange={(e) => {
                    onChange(e.target.value ? new Date(`${e.target.value}T00:00:00`) : undefined);
                  }}
                  type="date"
                  value={isDefined(value) ? formatDate(value, 'yyyy-MM-dd') : ''}
                />
              );
            }}
            rules={{
              required: 'End Date is required',
              validate: (value) => {
                if (isNotDefined(startDate) || isNotDefined(value)) {
                  return true;
                }

                return value >= startDate || 'End Date must be on or after Start Date';
              },
            }}
          />
        </InputGroup>
        <FormErrorMessage>{errors.endDate?.message}</FormErrorMessage>
      </FormControl>
    </Flex>
  );
};

export default BillingDatePicker;
